import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { money } from "@/components/shared";
import { BarChart3 } from "lucide-react";

const SELECT = "border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand";

function ChartTooltip({ active, payload, label, cleaners }) {
  if (!active || !payload?.length) return null;
  const rows = cleaners.map((c) => ({
    ...c, completed: payload.find((p) => p.dataKey === `${c.id}_completed`)?.value || 0,
    booked: payload.find((p) => p.dataKey === `${c.id}_booked`)?.value || 0,
  })).filter((r) => r.completed || r.booked);
  return (
    <div className="rounded-xl border border-border/60 bg-card px-4 py-3 shadow-md text-xs space-y-1">
      <div className="font-medium text-sm mb-1">{label}</div>
      {rows.length === 0 && <div className="text-muted-foreground">No bookings</div>}
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
          <span className="flex-1">{r.name}</span>
          <span>{money(r.completed)} done · {money(r.booked)} booked</span>
        </div>
      ))}
    </div>
  );
}

export default function RevenueChart() {
  const [data, setData] = useState(null);
  const [cleaner, setCleaner] = useState("");
  useEffect(() => { api.get("/reports/revenue-chart", { params: { months: 6 } }).then((r) => setData(r.data)).catch(() => {}); }, []);

  const cleaners = useMemo(() => (data?.cleaners || []).filter((c) => !cleaner || c.id === cleaner), [data, cleaner]);
  const rows = useMemo(() => (data?.months || []).map((m) => {
    const row = { label: m.label.split(" ")[0] };
    cleaners.forEach((c) => {
      row[`${c.id}_completed`] = m.values[c.id]?.completed || 0;
      row[`${c.id}_booked`] = m.values[c.id]?.booked || 0;
    });
    return row;
  }), [data, cleaners]);
  const total = rows.reduce((s, r) => s + Object.values(r).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0), 0);

  return (
    <div className="mt-12" data-testid="revenue-chart">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="label-caps text-muted-foreground flex items-center gap-2"><BarChart3 className="w-4 h-4 text-brand" /> Revenue by cleaner · last 6 months (ex GST)</div>
        <select value={cleaner} onChange={(e) => setCleaner(e.target.value)} className={SELECT} data-testid="revenue-chart-cleaner">
          <option value="">All cleaners</option>
          {(data?.cleaners || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="rounded-sm border border-border/60 bg-card p-6">
        <div className="h-[300px]" data-testid="revenue-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="hsl(330 30% 92%)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} />
              <Tooltip cursor={{ fill: "hsl(330 80% 96%)" }} content={<ChartTooltip cleaners={cleaners} />} />
              {cleaners.flatMap((c) => [
                <Bar key={`${c.id}_completed`} dataKey={`${c.id}_completed`} stackId="rev" fill={c.color} />,
                <Bar key={`${c.id}_booked`} dataKey={`${c.id}_booked`} stackId="rev" fill={c.color} fillOpacity={0.3} />,
              ])}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs text-muted-foreground">
          {cleaners.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />{c.name}</span>
          ))}
          <span className="ml-auto inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-brand" /> Completed</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-brand/30" /> Booked</span>
            <span data-testid="revenue-chart-total">Total {money(total)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
